import { Button } from "@/components/ui/button";
import { Camera, MapPin, Star } from "lucide-react";
import { useState, useEffect } from "react";
import { BookingModal } from "./BookingModal";
import { ServiceSelectionModal } from "./ServiceSelectionModal";
import { supabase } from "@/integrations/supabase/client";
import { getOptimizedImageUrl } from "@/lib/utils";
import { useTranslation } from "react-i18next";

import { LocalizedContent } from "@/types";
import { useLocalized } from "@/hooks/useLocalized";

interface Service {
  id: string;
  photographer_id: string;
  name: string;
  category: string;
  description: string;
  duration_minutes: number;
  base_price: number;
  media_urls: string[];
}

interface HeroSectionProps {
  backgroundImage?: string;
  displayName: string;
  tagline: LocalizedContent;
  location?: LocalizedContent;
  rating?: number;
  reviewCount?: number;
}

export function HeroSection({
  backgroundImage = "https://images.unsplash.com/photo-1516035069371-29a1b244cc32?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80",
  displayName,
  tagline,
  location,
  rating = 0,
  reviewCount = 0
}: HeroSectionProps) {
  const { t } = useTranslation();
  const { getLocalized } = useLocalized();
  const [isServiceSelectionOpen, setIsServiceSelectionOpen] = useState(false);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [banners, setBanners] = useState<string[]>([backgroundImage]);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);


  useEffect(() => {
    const fetchBanners = async () => {
      const { data, error } = await supabase
        .from('hero_banners')
        .select('image_url')
        .eq('is_active', true)
        .order('sort_order');

      if (!error && data && data.length > 0) {
        setBanners(data.map(b => b.image_url));
      }
    };

    fetchBanners();
  }, []);

  useEffect(() => {
    if (banners.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentBannerIndex((prev) => (prev + 1) % banners.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [banners.length]);

  const handleBookSession = () => {
    setIsServiceSelectionOpen(true);
  };

  const handleServiceSelect = (service: Service) => {
    setSelectedService(service);
    setIsBookingModalOpen(true);
  };

  const handleViewPortfolio = () => {
    const portfolioSection = document.getElementById('portfolio');
    portfolioSection?.scrollIntoView({ behavior: 'smooth' });
  };
  return (
    <section className="relative h-screen flex items-center justify-center overflow-hidden">
      {/* Background Images Slideshow */}
      {/* Background Images Slideshow */}
      {banners.map((banner, index) => (
        <img
          key={index}
          src={getOptimizedImageUrl(banner, { width: 1920, quality: 85 })}
          alt={`Hero slide ${index + 1}`}
          className={`absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-1000 ${index === currentBannerIndex ? 'opacity-100' : 'opacity-0'
            }`}
        />
      ))}

      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/60" />

      {/* Content */}
      <div className="relative z-10 text-center text-white px-4 sm:px-6 max-w-4xl mx-auto">
        <div className="flex items-center justify-center gap-2 mb-3 sm:mb-4">
          <Camera className="h-6 w-6 sm:h-8 sm:w-8 text-[hsl(var(--gold))]" />
          <span className="text-sm sm:text-lg font-medium tracking-wider uppercase">Photography</span>
        </div>

        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold mb-4 sm:mb-6 tracking-tight">
          {displayName}
        </h1>

        <p className="text-base sm:text-lg md:text-xl lg:text-2xl mb-6 sm:mb-8 opacity-90 font-light px-2">
          {getLocalized(tagline)}
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 mb-8 sm:mb-12 text-sm sm:text-base">
          {location && (
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 sm:h-5 sm:w-5 text-[hsl(var(--gold))]" />
              <span className="text-base sm:text-lg">{getLocalized(location as any)}</span>
            </div>
          )}

          {reviewCount > 0 && (
            <div className="flex items-center gap-2">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`h-4 w-4 sm:h-5 sm:w-5 ${i < Math.floor(rating) ? 'text-[hsl(var(--gold))] fill-current' : 'text-white/30'}`}
                  />
                ))}
              </div>
              <span className="text-base sm:text-lg">{rating} ({reviewCount} {t('testimonials.reviews', 'reviews')})</span>
            </div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
          <Button
            size="lg"
            onClick={handleBookSession}
            className="bg-[hsl(var(--gold))] hover:bg-[hsl(var(--gold-dark))] text-black font-semibold px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg transition-all duration-300 shadow-[var(--shadow-gold)] w-full sm:w-auto"
          >
            {t('hero.cta', 'Book Session')}
          </Button>
          <Button
            variant="outline"
            size="lg"
            onClick={handleViewPortfolio}
            className="border-white/30 text-black hover:bg-white hover:text-primary px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg backdrop-blur-sm w-full sm:w-auto"
          >
            {t('hero.viewPortfolio', 'View Portfolio')}
          </Button>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-5 h-8 sm:w-6 sm:h-10 border-2 border-white/50 rounded-full flex justify-center">
          <div className="w-1 h-2 sm:h-3 bg-white/50 rounded-full mt-1.5 sm:mt-2 animate-pulse"></div>
        </div>
      </div>

      <ServiceSelectionModal
        isOpen={isServiceSelectionOpen}
        onClose={() => setIsServiceSelectionOpen(false)}
        onServiceSelect={handleServiceSelect}
      />

      <BookingModal
        isOpen={isBookingModalOpen}
        onClose={() => setIsBookingModalOpen(false)}
        service={selectedService}
      />
    </section>
  );
}